import { ColorTypes } from '@omegagrid/core';
import { FormAdapter, FormAlert, FormContainerOptions, FormControlType, FormValues, FormControlProperties } from '@omegagrid/form';
import { msg } from "@omegagrid/localize";
import rp from '@reporting/rp-app-host';
import { Embedding } from '@reporting/rp-types/accounting';
import { EmbedModule, EMBEDDING_URL } from '../types';

export class EmbedFormAdapter extends FormAdapter {

	private embedding: Embedding | null = null;

	constructor(
		private module: EmbedModule,
		private idReport: number,
	) { super() }

	private get embedUrl(): string | null {
		if (!this.embedding?.id_embedding) return null;
		return EMBEDDING_URL
			.replace('{a}', this.embedding.accounting.toString())
			.replace('{m}', this.module.toLowerCase())
			.replace('{id}', this.embedding.id_embedding);
	}

	getColumns() {
		return [
			{ width: 240 },
			{ width: null },
		];
	}

	async getControls(): Promise<FormControlProperties[]> {
		this.embedding = await rp.ms('accounting').get<Embedding>(`embeddings/${this.idReport}`, { module: this.module });
		const controls: FormControlProperties[] = [
			{ row: 0, col: 0, type: FormControlType.Label, value: msg('Enabled'), for: 'enabled' },
			{ row: 0, col: 1, type: FormControlType.Checkbox, key: 'enabled', value: !!this.embedding },
			{ row: 1, col: 0, type: FormControlType.Label, value: msg('Readonly'), for: 'readonly' },
			{ row: 1, col: 1, type: FormControlType.Checkbox, key: 'readonly', value: this.embedding ? !!this.embedding.readonly : true },
		];
		return controls;
	}

	getOptions(): FormContainerOptions {
		return {
			caption: null,
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			closeAfterSave: false,
			buttons: [
				{
					key: 'openLink',
					text: msg('Open'),
					color: ColorTypes.Blue,
					icon: 'arrow-up-right-from-square'
				},
				{
					key: 'copyLink',
					text: msg('Copy'),
					color: ColorTypes.Yellow,
					icon: 'link'
				},
				"save",
			]
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean|FormAlert[]> {
		const formValues = values.get(0);
		const enabled = !!formValues.enabled;
		const readonly = !!formValues.readonly;

		if (enabled) {
			this.embedding = await rp.ms('accounting').post<Embedding>(`embeddings`, {
				id_report: this.idReport,
				module: this.module,
				readonly,
			});
			if (!this.embedding.id_embedding) return Promise.reject(new Error('Failed to create embedding'));
			this.copyLink();
		} else if (this.embedding) {
			await rp.ms('accounting').delete(`embeddings/${this.idReport}`, { module: this.module });
			this.embedding = null;
		}
		return true;
	}

	onAction(action: string): void {
		if (!this.embedUrl) {
			rp.host.showAlert({ type: 'warning', title: msg('Embedding'), content: msg('Embedding is not enabled') });
			return;
		}
		switch (action) {
			case 'openLink':
				window.open(this.embedUrl, '_blank');
				break;
			case 'copyLink':
				this.copyLink();
				break;
		}
	}

	copyLink() {
		navigator.clipboard.writeText(this.embedUrl);
		rp.host.showAlert({ type: 'success', title: msg('Embedding'), content: msg('Copied to clipboard') });
	}

};
